import { apiGet, apiPost } from '../utils/apiClient';

/**
 * AI 분석 결과 캐시 (1분)
 */
let aiAnalysisCache: {
  [userKey: number]: {
    data: ParsedUserTradingAnalysis | null;
    timestamp: number;
  };
} = {};

const AI_ANALYSIS_CACHE_DURATION = 1 * 60 * 1000; // 1분

/**
 * AI 분석 결과 캐시 무효화 (새로운 분석 완료 시 호출)
 */
export const invalidateAIAnalysisCache = (userKey: number): void => {
  delete aiAnalysisCache[userKey];
  console.log(`🗑️ AI 분석 캐시 무효화 (userKey: ${userKey})`);
};

export interface TradingStatistics {
  totalTrades: number;
  buyTrades: number;
  sellTrades: number;
  totalProfit: number;
  profitRate: number;
  averageHoldingPeriod: number;
  winRate: number;
  averageWin: number;
  averageLoss: number;
  maxDrawdown: number;
  sharpeRatio?: number | null;
  profitFactor?: number | null;
  kellyCriterion?: number | null;
  riskRewardRatio?: number | null;
  standardDeviation?: number | null;
  var95?: number | null;
  expectedShortfall?: number | null;
}

export interface TradingAnalysisFeedback {
  summary?: string;
  strengths?: string[];
  weaknesses?: string[];
  recommendations?: string[];
  riskAssessment?: string;
  statisticalInsights?: string;
  tradingStyle?: string;
  [key: string]: any; // JSON 구조가 유동적일 수 있음
}

export interface AIAnalysisResult {
  statistics: TradingStatistics;
  feedback: string; // JSON 문자열
  model: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  cached?: boolean;
}

export interface ParsedAIAnalysis {
  statistics: TradingStatistics;
  feedback: TradingAnalysisFeedback;
  model: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  cached?: boolean;
}

/**
 * AI 분석 요청 (단일 매매내역)
 */
export async function analyzeTradingHistory(
  apiBaseUrl: string,
  historyId: number,
  model?: string
): Promise<ParsedAIAnalysis> {
  const response = await apiPost<{ success: boolean; data: AIAnalysisResult }>(
    `${apiBaseUrl}/api/groq/analyze-trading`,
    {
      historyId,
      model: model || 'llama-3.3-70b-versatile',
    }
  );

  const result = response.data || response;

  // feedback이 JSON 문자열인 경우 파싱
  let parsedFeedback: TradingAnalysisFeedback = {};
  if (typeof result.feedback === 'string') {
    try {
      parsedFeedback = JSON.parse(result.feedback);
    } catch (e) {
      // JSON 파싱 실패 시 문자열 그대로 사용
      parsedFeedback = { summary: result.feedback };
    }
  } else if (typeof result.feedback === 'object') {
    parsedFeedback = result.feedback;
  }

  return {
    statistics: result.statistics,
    feedback: parsedFeedback,
    model: result.model,
    usage: result.usage,
    cached: result.cached,
  };
}

export interface UserTradingAnalysisResult {
  overallStatistics: {
    totalSessions: number;
    totalProfit: number;
    averageProfitRate: number;
    averageWinRate: number;
    bestSession: number;
    worstSession: number;
  };
  sessionSummaries: Array<{
    historyId: number;
    statistics: TradingStatistics;
    dateRange: string;
    stockCode: string;
    stockName: string;
  }>;
  feedback: string; // JSON 문자열
  model: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface ParsedUserTradingAnalysis {
  overallStatistics: {
    totalSessions: number;
    totalProfit: number;
    averageProfitRate: number;
    averageWinRate: number;
    bestSession: number;
    worstSession: number;
  };
  sessionSummaries: Array<{
    historyId: number;
    statistics: TradingStatistics;
    dateRange: string;
    stockCode: string;
    stockName: string;
  }>;
  feedback: TradingAnalysisFeedback;
  model: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

/**
 * 사용자 최근 매매내역 종합 분석 (백엔드에서 자동 처리)
 */
export async function analyzeUserTradingHistory(
  apiBaseUrl: string,
  userKey: number,
  limit?: number,
  model?: string
): Promise<ParsedUserTradingAnalysis> {
  const response = await apiPost<{ success: boolean; data: UserTradingAnalysisResult }>(
    `${apiBaseUrl}/api/groq/analyze-user-trading`,
    {
      userKey,
      limit: limit || 10,
      model: model || 'llama-3.3-70b-versatile',
    }
  );

  const result = response.data || response;

  // 분석 결과 검증
  if (!result) {
    throw new Error('분석 결과를 받을 수 없습니다. 서버 응답이 비어있습니다.');
  }

  if (!result.overallStatistics) {
    throw new Error('분석 결과가 불완전합니다. 전체 통계 정보가 없습니다.');
  }

  if (!result.sessionSummaries || !Array.isArray(result.sessionSummaries) || result.sessionSummaries.length === 0) {
    throw new Error('분석 결과가 불완전합니다. 세션 요약 정보가 없습니다.');
  }

  // feedback이 JSON 문자열인 경우 파싱
  let parsedFeedback: TradingAnalysisFeedback = {};
  if (typeof result.feedback === 'string') {
    try {
      const parsed = JSON.parse(result.feedback);
      parsedFeedback = parsed || {};
      console.log('피드백 파싱 성공:', parsedFeedback);
    } catch (e) {
      console.error('피드백 JSON 파싱 실패:', e, '원본:', result.feedback);
      // JSON 파싱 실패 시 문자열 그대로 사용
      parsedFeedback = { summary: result.feedback };
    }
  } else if (typeof result.feedback === 'object') {
    parsedFeedback = result.feedback || {};
    console.log('피드백 객체 (파싱 불필요):', parsedFeedback);
  } else {
    console.warn('피드백 데이터가 없거나 예상치 못한 형식:', result.feedback);
    // 피드백이 없어도 분석 결과는 반환 (경고만)
  }

  console.log('최종 파싱된 피드백:', parsedFeedback);

  const parsedResult: ParsedUserTradingAnalysis = {
    overallStatistics: result.overallStatistics,
    sessionSummaries: result.sessionSummaries,
    feedback: parsedFeedback,
    model: result.model,
    usage: result.usage,
  };

  // 새로운 분석 결과를 캐시에 저장
  aiAnalysisCache[userKey] = {
    data: parsedResult,
    timestamp: Date.now(),
  };
  console.log(`💾 새로운 AI 분석 결과 캐시 저장 (userKey: ${userKey})`);

  return parsedResult;
}

/**
 * 사용자의 최신 AI 분석 결과 조회 (캐시 적용)
 */
export async function getLatestAnalysisResult(
  apiBaseUrl: string,
  userKey: number,
  forceRefresh = false
): Promise<ParsedUserTradingAnalysis | null> {
  // 캐시 확인
  const cached = aiAnalysisCache[userKey];
  if (
    !forceRefresh &&
    cached &&
    Date.now() - cached.timestamp < AI_ANALYSIS_CACHE_DURATION &&
    cached.data !== null
  ) {
    console.log(`✅ AI 분석 캐시 HIT (userKey: ${userKey})`);
    return cached.data;
  }

  try {
    // apiGet은 responseData.data를 반환하므로, 여기서는 UserTradingAnalysisResult | null을 받습니다
    const result = await apiGet<UserTradingAnalysisResult | null>(
      `${apiBaseUrl}/api/groq/latest-analysis/${userKey}`
    );

    // result가 null인 경우 (분석 결과가 없음)
    if (!result) {
      // null도 캐시에 저장 (불필요한 재조회 방지)
      aiAnalysisCache[userKey] = {
        data: null,
        timestamp: Date.now(),
      };
      return null;
    }

    // feedback이 JSON 문자열인 경우 파싱
    let parsedFeedback: TradingAnalysisFeedback = {};
    if (typeof result.feedback === 'string') {
      try {
        const parsed = JSON.parse(result.feedback);
        parsedFeedback = parsed || {};
      } catch (e) {
        console.error('피드백 JSON 파싱 실패:', e, '원본:', result.feedback);
        parsedFeedback = { summary: result.feedback };
      }
    } else if (typeof result.feedback === 'object') {
      parsedFeedback = result.feedback || {};
    }

    const parsedResult: ParsedUserTradingAnalysis = {
      overallStatistics: result.overallStatistics,
      sessionSummaries: result.sessionSummaries,
      feedback: parsedFeedback,
      model: result.model,
      usage: result.usage,
    };

    // 성공한 데이터만 캐시 저장
    aiAnalysisCache[userKey] = {
      data: parsedResult,
      timestamp: Date.now(),
    };
    console.log(`💾 AI 분석 캐시 저장 (userKey: ${userKey})`);

    return parsedResult;
  } catch (error: any) {
    console.error('최신 분석 결과 조회 중 에러:', error);
    // 에러 발생 시 null 반환 (분석 결과가 없는 것으로 처리)
    return null;
  }
}

